import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { environment } from "environments/environment";
import { Observable } from "rxjs";

import {
  RequestProjectionEnvelope,
  VenueMatchmakingControlEnvelope,
  VenueMatchmakingUiError,
} from "./venue-matchmaking.types";

const RETRYABLE_CODES = new Set([
  "RATE_LIMITED",
  "TEMPORARY_UNAVAILABLE",
  "TRANSACTION_RETRYABLE",
]);

const ERROR_MESSAGES: Record<string, string> = {
  FORBIDDEN:
    "You do not have permission to manage Match Making for this booking.",
  NOT_FOUND: "This booking is not available.",
  FEATURE_DISABLED: "Match Making is not available for this booking.",
  APPROVAL_REQUIRED: "Match Making is not available for this booking.",
  IDEMPOTENCY_CONFLICT:
    "This Match Making action conflicts with an earlier request. Refresh and try again.",
  ILLEGAL_TRANSITION:
    "The booking changed. Checking its current Match Making status.",
  ELIGIBILITY_UNAVAILABLE:
    "Match Making eligibility could not be verified for this booking.",
  PRIVACY_GATE_FAILED:
    "Match Making eligibility could not be verified for this booking.",
  CAPACITY_UNAVAILABLE:
    "Player capacity could not be verified for this booking.",
  QUERY_PLAN_REJECTED:
    "Match Making eligibility could not be verified for this booking.",
  RATE_LIMITED: "Match Making could not start yet. Try again shortly.",
  TEMPORARY_UNAVAILABLE: "Match Making could not start yet. Try again.",
  TRANSACTION_RETRYABLE: "Match Making could not start yet. Try again.",
  BAD_REQUEST:
    "This booking cannot start Match Making with its current details.",
};

@Injectable({ providedIn: "root" })
export class VenueMatchmakingService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/matchmaking/bookings`;

  getVenueMatchmakingControl(
    bookingId: string,
  ): Observable<VenueMatchmakingControlEnvelope> {
    return this.http.get<VenueMatchmakingControlEnvelope>(
      `${this.baseUrl}/${encodeURIComponent(bookingId)}/venue-control`,
    );
  }

  triggerVenueMatchmaking(
    bookingId: string,
    idempotencyKey: string,
  ): Observable<RequestProjectionEnvelope> {
    const headers = new HttpHeaders({ "Idempotency-Key": idempotencyKey });
    return this.http.post<RequestProjectionEnvelope>(
      `${this.baseUrl}/${encodeURIComponent(bookingId)}/venue-control/trigger`,
      {},
      { headers },
    );
  }

  normalizeError(error: unknown): VenueMatchmakingUiError {
    const httpError = error as HttpErrorResponse;
    const payload = httpError?.error;
    const code = String(
      payload?.code ?? payload?.error?.code ?? "UNKNOWN_ERROR",
    );
    const retryable = RETRYABLE_CODES.has(code);
    const retryAfterSeconds = this.retryAfterSeconds(httpError);
    const baseMessage =
      ERROR_MESSAGES[code] ??
      "Match Making could not be updated. Manual invitations are still available.";

    return {
      code,
      message:
        code === "RATE_LIMITED" && retryAfterSeconds !== undefined
          ? `Match Making could not start yet. Try again in ${retryAfterSeconds} seconds.`
          : baseMessage,
      retryable,
      reconcile: code === "ILLEGAL_TRANSITION",
      retryAfterSeconds,
    };
  }

  private retryAfterSeconds(error: HttpErrorResponse): number | undefined {
    const value = error?.headers?.get?.("Retry-After");
    if (!value) return undefined;

    const seconds = Number(value);
    if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds);

    const retryAt = Date.parse(value);
    if (Number.isNaN(retryAt)) return undefined;
    return Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
  }
}

export function createVenueMatchmakingIdempotencyKey(): string {
  const randomUuid = globalThis.crypto?.randomUUID?.();
  const entropy = randomUuid ?? Math.random().toString(36).slice(2);
  return `venue-matchmaking-${Date.now()}-${entropy}`.slice(0, 128);
}
